import * as React from 'react';
import { CREDIT_LOW_USAGE_PERCENT } from '@insio/shared-types';
import { cn } from '../utils';
import { Avatar, Divider, Icon, Popover, ProgressBar, Select } from '../components';
import type { IconName } from '../icons/names';
import { useTranslation } from '../providers/UIProvider';

/** What the profile menu's usage panel counts. */
export const ProfileMenuUsageUnit = {
  DOCUMENTS: 'DOCUMENTS',
  CREDITS: 'CREDITS'
} as const;

export type ProfileMenuUsageUnit = (typeof ProfileMenuUsageUnit)[keyof typeof ProfileMenuUsageUnit];

/** Extraction quota or credit usage shown in the profile menu. */
export interface ProfileMenuUsage {
  /** What is counted; defaults to `DOCUMENTS`. */
  unit?: ProfileMenuUsageUnit;
  /** Documents extracted, or credits drawn, in the current billing window. */
  used: number;
  /** Monthly document limit or period credit allotment; `null` means unlimited or no running period. */
  limit: number | null;
  /** Localized date the limit resets or the period ends on; `null` when there is no running period. */
  resetDate: string | null;
  /** `false` when the period will not renew — the date is shown as its end. Defaults to `true`. */
  renews?: boolean;
  /** Credits only: credits beyond the period allotment, shown as "+N extra". */
  extraRemaining?: number | null;
  /** Credits only: every credit available now, shown when there is no running period. */
  available?: number | null;
  /** Credits only: too few credits left to start AI work. */
  exhausted?: boolean;
  /** Current plan name, e.g. `FREE`. */
  planName?: string | null;
  /** Route to the billing & plan settings. */
  planHref?: string;
}

/** Navigation entry rendered in the profile menu list. */
export interface ProfileMenuEntry {
  icon: IconName;
  label: string;
  href?: string;
  onClick?: () => void;
}

/** Accounts the signed-in user can act in. Omit when they belong to only one. */
export interface ProfileMenuAccounts {
  activeId: string;
  /**
   * Entries to switch into. `pinned` entries are listed first, set apart from the rest by a
   * divider; `icon` replaces the default building icon.
   */
  items: { id: string; name: string; icon?: IconName; pinned?: boolean }[];
  /** Requests a switch. The host may confirm before acting on it. */
  onSwitch: (accountId: string) => void;
  /** A pick awaiting confirmation — shown as selected until it is confirmed or dropped. */
  pendingId?: string | null;
  /** A switch is in flight — rows stay inert until the reload lands. */
  switching?: boolean;
}

/** Invitations waiting for the signed-in user. Omit when there are none. */
export interface ProfileMenuInvitations {
  /** `detail` is a second line under the name, e.g. the role the invitation offers. */
  items: { id: string; accountName: string; detail?: string }[];
  onAccept: (invitationId: string) => void;
}

export interface ProfileMenuProps {
  user?: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
  };
  /** Account switcher. Rendered only when there is more than one account to pick from. */
  accounts?: ProfileMenuAccounts | null;
  /** Pending invitations, each joinable with one click. */
  invitations?: ProfileMenuInvitations | null;
  /** Quota panel content. Omitted entirely when not provided. */
  usage?: ProfileMenuUsage | null;
  entries?: ProfileMenuEntry[];
  myAccountHref?: string;
  onMyAccountClick?: () => void;
  logout?: () => void;
  /** Router link component (e.g. react-router `Link`) used to render entries as SPA links. */
  linkComponent?: React.ElementType;
  className?: string;
  testId?: string;
}

/** Usage as a 0-100 percentage of the limit, or `null` for unlimited quotas. */
function usagePercent(usage: ProfileMenuUsage): number | null {
  if (usage.limit === null || usage.limit === 0) return null;
  return Math.min((usage.used / usage.limit) * 100, 100);
}

/** Mirrors the billing page thresholds so both surfaces colour the quota the same way. */
function usageVariant(pct: number | null): 'success' | 'warn' | 'error' | 'neutral' {
  if (pct === null) return 'neutral';
  if (pct >= 80) return 'error';
  if (pct >= 50) return 'warn';
  return 'success';
}

/** Credit usage colours: error only when exhausted, warning from the LOW threshold. */
function creditUsageVariant(pct: number | null, exhausted: boolean): 'success' | 'warn' | 'error' {
  if (exhausted) return 'error';
  if (pct !== null && pct >= CREDIT_LOW_USAGE_PERCENT) return 'warn';
  return 'success';
}

/** Shared by every profile menu row so the desktop popover and the mobile drawer match. */
const rowClassName =
  'flex w-full items-center gap-md px-md py-sm text-left no-underline text-inherit transition-colors hover:bg-background-muted';

/** Chevron pointing right — the icon set only ships the down variant. */
function ChevronRight({ className }: { className?: string }) {
  return (
    <Icon
      name='chevron-down'
      rotate={270}
      size='sm'
      className={cn('shrink-0 text-foreground-muted', className)}
    />
  );
}

export interface ProfileUsagePanelProps {
  usage: ProfileMenuUsage;
  /** Router link component (e.g. react-router `Link`) used to render the plan link. */
  linkComponent?: React.ElementType;
  /** Fired when the plan link is followed — closes the surrounding menu or drawer. */
  onNavigate?: () => void;
  className?: string;
}

/**
 * Usage block: plan, used/limit, progress and reset date, for documents or credits.
 * Shared by the desktop profile menu and the mobile drawer.
 */
export function ProfileUsagePanel({
  usage,
  linkComponent,
  onNavigate,
  className
}: ProfileUsagePanelProps) {
  const t = useTranslation('modules.menu');

  const isCredits = usage.unit === ProfileMenuUsageUnit.CREDITS;
  const exhausted = usage.exhausted === true;
  const pct = usagePercent(usage);
  const remaining = usage.limit !== null ? Math.max(usage.limit - usage.used, 0) : null;
  const extraRemaining = isCredits && usage.limit !== null ? (usage.extraRemaining ?? 0) : 0;
  const usedLabel = isCredits
    ? usage.limit === null
      ? t('usage.creditsAvailable', { count: usage.available ?? 0 })
      : t('usage.creditsCount', { used: Math.min(usage.used, usage.limit), limit: usage.limit })
    : usage.limit === null
      ? t('usage.countUnlimited', { used: usage.used })
      : t('usage.count', { used: Math.min(usage.used, usage.limit), limit: usage.limit });
  const variant = isCredits ? creditUsageVariant(pct, exhausted) : usageVariant(pct);

  let periodLabel: string | null = null;
  if (usage.resetDate === null) {
    periodLabel = isCredits ? t('usage.noPeriod') : null;
  } else if (usage.renews === false) {
    periodLabel = t('usage.endsOn', { date: usage.resetDate });
  } else {
    periodLabel = t(isCredits ? 'usage.creditsRenewOn' : 'usage.resetsOn', {
      date: usage.resetDate
    });
  }

  const LinkComponent = linkComponent ?? 'a';
  const linkProps = linkComponent ? { to: usage.planHref } : { href: usage.planHref };

  return (
    <div className={cn('flex flex-col gap-2xs px-md py-sm', className)}>
      <div className='flex items-center justify-between gap-sm'>
        <span className='text-xs font-medium uppercase tracking-wide text-foreground-muted'>
          {t(isCredits ? 'usage.creditsTitle' : 'usage.title')}
        </span>
        {usage.planName &&
          (usage.planHref ? (
            <LinkComponent
              {...linkProps}
              onClick={onNavigate}
              className='flex shrink-0 items-center gap-xs text-xs font-medium text-foreground-base no-underline hover:text-secondary-base'
            >
              {t('usage.plan', { name: usage.planName })}
              <ChevronRight />
            </LinkComponent>
          ) : (
            <span className='shrink-0 text-xs font-medium text-foreground-base'>
              {t('usage.plan', { name: usage.planName })}
            </span>
          ))}
      </div>

      <div className='flex items-baseline justify-between gap-sm'>
        <span className='text-lg font-semibold tabular-nums whitespace-nowrap text-foreground-base'>
          {usedLabel}
        </span>
        {remaining !== null &&
          (isCredits ? (
            // Wraps on its own, so the figure beside it keeps one line.
            <span className='flex min-w-0 flex-wrap items-baseline justify-end gap-x-xs text-right text-xs text-foreground-muted'>
              <span>{t('usage.creditsRemaining', { count: remaining })}</span>
              {extraRemaining > 0 && (
                <span className='font-medium text-foreground-base'>
                  {t('usage.extraRemaining', { credits: extraRemaining })}
                </span>
              )}
            </span>
          ) : (
            <span className='shrink-0 text-xs text-foreground-muted'>
              {t('usage.remaining', { count: remaining })}
            </span>
          ))}
      </div>

      {pct !== null && (
        <ProgressBar
          value={pct}
          variant={variant}
          ariaLabel={t(isCredits ? 'usage.creditsProgressAriaLabel' : 'usage.progressAriaLabel', {
            label: usedLabel
          })}
        />
      )}

      {isCredits && exhausted && (
        <span className='text-xs font-medium text-error-text'>{t('usage.exhausted')}</span>
      )}

      {periodLabel !== null && <span className='text-xs text-foreground-muted'>{periodLabel}</span>}
    </div>
  );
}

export interface ProfileAccountSelectProps {
  accounts: ProfileMenuAccounts;
  /** Fired after a switch is requested — closes the surrounding menu or drawer. */
  onAfterSelect?: () => void;
  /** Section caption. Hidden where the surrounding control already names the section. */
  showTitle?: boolean;
  className?: string;
}

/**
 * Account switcher: a combo box listing every account, the active one selected.
 * Shared by the desktop profile menu and the mobile drawer.
 */
export function ProfileAccountSelect({
  accounts,
  onAfterSelect,
  showTitle = true,
  className
}: ProfileAccountSelectProps) {
  const t = useTranslation('modules.menu');

  const options = accounts.items.map(account => ({
    value: account.id,
    label: account.name,
    icon: account.icon ?? ('building' as IconName),
    preferred: account.pinned
  }));

  return (
    <div className={cn('flex flex-col gap-xs px-md py-sm', className)}>
      {showTitle && (
        <span className='text-xs font-medium uppercase tracking-wide text-foreground-muted'>
          {t('accounts.title')}
        </span>
      )}
      <Select
        height='sm'
        variant='white'
        ariaLabel={t('accounts.title')}
        disabled={accounts.switching}
        // The pending pick drives the display so dropping it puts the combo box back on the
        // account the session is actually in.
        value={accounts.pendingId ?? accounts.activeId}
        options={options}
        onChange={value => {
          const accountId = String(value);
          if (accountId === accounts.activeId) return;
          accounts.onSwitch(accountId);
          onAfterSelect?.();
        }}
      />
    </div>
  );
}

/** An invitation's name, with its detail (if any) as a muted second line. */
function InvitationLabel({ name, detail }: { name: string; detail?: string }) {
  return (
    <span className='flex min-w-0 flex-col'>
      <span className='truncate font-medium text-foreground-base'>{name}</span>
      {detail && <span className='truncate text-xs text-foreground-muted'>{detail}</span>}
    </span>
  );
}

export interface ProfileInvitationsPanelProps {
  invitations: ProfileMenuInvitations;
  /** Fired after a join is requested — closes the surrounding menu or drawer. */
  onAfterSelect?: () => void;
  className?: string;
}

/**
 * Pending-invitation block for the mobile drawer. Deliberately a plain list with no
 * collapse: a person has a handful of these at most, and they should not be hidden.
 */
export function ProfileInvitationsPanel({
  invitations,
  onAfterSelect,
  className
}: ProfileInvitationsPanelProps) {
  const t = useTranslation('modules.menu');

  if (invitations.items.length === 0) return null;

  return (
    <div className={cn('flex flex-col', className)}>
      <span className='px-md pt-sm text-xs font-medium uppercase tracking-wide text-foreground-muted'>
        {t('invitations.title')}
      </span>
      {invitations.items.map(invitation => (
        <button
          key={invitation.id}
          type='button'
          aria-label={t('invitations.joinAria', { name: invitation.accountName })}
          className='flex w-full items-center gap-md px-md py-sm text-left transition-colors hover:bg-background-muted'
          onClick={() => {
            invitations.onAccept(invitation.id);
            onAfterSelect?.();
          }}
        >
          <Icon name='envelope' className='w-5 shrink-0 text-center' />
          <InvitationLabel name={invitation.accountName} detail={invitation.detail} />
          <span className='ml-auto shrink-0 text-xs font-medium text-secondary-base'>
            {t('invitations.join', { name: invitation.accountName })}
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * Desktop account menu: profile header, extraction quota, settings links and logout.
 */
export const ProfileMenu = React.forwardRef<HTMLDivElement, ProfileMenuProps>(
  (
    {
      user,
      accounts,
      invitations,
      usage,
      entries = [],
      myAccountHref,
      onMyAccountClick,
      logout,
      linkComponent,
      className,
      testId
    },
    ref
  ) => {
    const t = useTranslation('modules.menu');

    // Drops the Divider's default bottom margin so row hover reaches the separator line.
    const dividerClassName = 'mb-0';

    // Renders a menu row as an SPA link, a plain anchor or a button depending on what it was given.
    const renderRow = (
      opts: {
        href?: string;
        onClick?: () => void;
        className?: string;
        ariaLabel?: string;
        children: React.ReactNode;
      },
      close: () => void
    ) => {
      const { href, onClick, className: rowClass, ariaLabel, children } = opts;

      if (href) {
        const LinkComponent = linkComponent ?? 'a';
        const linkProps = linkComponent ? { to: href } : { href };
        return (
          <LinkComponent
            {...linkProps}
            aria-label={ariaLabel}
            className={cn(rowClassName, rowClass)}
            onClick={() => {
              onClick?.();
              close();
            }}
          >
            {children}
          </LinkComponent>
        );
      }

      return (
        <button
          type='button'
          aria-label={ariaLabel}
          className={cn(rowClassName, rowClass)}
          onClick={() => {
            onClick?.();
            close();
          }}
        >
          {children}
        </button>
      );
    };

    return (
      <Popover
        ref={ref}
        align='bottom end'
        size='lg'
        testId={testId}
        className={cn('overflow-hidden', className)}
        triggerButton={
          <button
            type='button'
            aria-label={t('profileMenu')}
            className='rounded-full outline-none transition-opacity cursor-pointer hover:opacity-80 focus-visible:ring-2 focus-visible:ring-primary-base focus-visible:ring-offset-2'
          >
            <Avatar src={user?.image ?? undefined} name={user?.name ?? undefined} size='sm' />
          </button>
        }
      >
        {({ close }) => (
          <div className='flex flex-col'>
            {renderRow(
              {
                href: myAccountHref,
                onClick: onMyAccountClick,
                ariaLabel: t('myAccount'),
                children: (
                  <>
                    <Avatar
                      src={user?.image ?? undefined}
                      name={user?.name ?? undefined}
                      size='md'
                    />
                    <div className='flex min-w-0 flex-1 flex-col'>
                      <span className='truncate font-semibold text-foreground-base'>
                        {user?.name ?? ''}
                      </span>
                      <span className='truncate text-sm text-foreground-muted'>
                        {user?.email ?? ''}
                      </span>
                    </div>
                    <ChevronRight />
                  </>
                )
              },
              close
            )}

            {accounts && accounts.items.length > 1 && (
              <>
                <Divider size='full' variant='light' thickness='sm' className={dividerClassName} />
                <ProfileAccountSelect accounts={accounts} onAfterSelect={close} />
              </>
            )}

            {invitations && invitations.items.length > 0 && (
              <>
                <Divider size='full' variant='light' thickness='sm' className={dividerClassName} />
                <div className='flex flex-col'>
                  <span className='px-md pt-sm text-xs font-medium uppercase tracking-wide text-foreground-muted'>
                    {t('invitations.title')}
                  </span>
                  {invitations.items.map(invitation => (
                    <React.Fragment key={invitation.id}>
                      {renderRow(
                        {
                          onClick: () => invitations.onAccept(invitation.id),
                          className: 'gap-md',
                          ariaLabel: t('invitations.joinAria', { name: invitation.accountName }),
                          children: (
                            <>
                              <Icon name='envelope' className='w-5 shrink-0 text-center' />
                              <InvitationLabel
                                name={invitation.accountName}
                                detail={invitation.detail}
                              />
                              <span className='ml-auto shrink-0 text-xs font-medium text-secondary-base'>
                                {t('invitations.join', { name: invitation.accountName })}
                              </span>
                            </>
                          )
                        },
                        close
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}

            {usage && (
              <>
                <Divider size='full' variant='light' thickness='sm' className={dividerClassName} />
                <ProfileUsagePanel usage={usage} linkComponent={linkComponent} onNavigate={close} />
              </>
            )}

            {entries.length > 0 && (
              <>
                <Divider size='full' variant='light' thickness='sm' className={dividerClassName} />
                <div className='flex flex-col'>
                  {entries.map(entry => (
                    <React.Fragment key={entry.label}>
                      {renderRow(
                        {
                          href: entry.href,
                          onClick: entry.onClick,
                          className: 'gap-md text-foreground-base',
                          children: (
                            <>
                              <Icon name={entry.icon} className='w-5 shrink-0 text-center' />
                              <span className='font-medium'>{entry.label}</span>
                            </>
                          )
                        },
                        close
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}

            {logout && (
              <>
                <Divider size='full' variant='light' thickness='sm' className={dividerClassName} />
                <div className='flex flex-col'>
                  {renderRow(
                    {
                      onClick: logout,
                      className: 'gap-md text-error-text hover:bg-error-bg',
                      children: (
                        <>
                          <Icon
                            name='arrow-right-from-bracket'
                            className='w-5 shrink-0 text-center'
                          />
                          <span className='font-medium'>{t('logout')}</span>
                        </>
                      )
                    },
                    close
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </Popover>
    );
  }
);

ProfileMenu.displayName = 'ProfileMenu';
